import { FunctionComponent, useEffect, useState } from 'react';
import QueryGate from 'src/designSystem/QueryGate';
import QueryErrorState from 'src/designSystem/QueryErrorState';
import { useGetUsers } from 'src/queries/users';
import PageLayout from 'src/layouts/PageLayout';
import UserSelect from 'src/layouts/UserSelect';
import TourTooltip from 'src/designSystem/TourTooltip';
import { createLocalIcon } from 'src/designSystem/LocalIcon';
import BaoSmallSvg from 'src/assets/onboarding/bao/bao-small.svg?react';
import useFamilyProfileSwitch from 'src/hooks/useFamilyProfileSwitch';
import useBaoGreeting from 'src/components/UserManagement/hooks/useBaoGreeting';
import type { User } from 'src/types/user.types';
import {
  markProfileChosen,
  useIsProfileChoiceNeeded,
} from 'src/utils/sharedDevice';
import { reportError } from 'src/modules/logs/Sentry';

const BaoSmall = createLocalIcon(BaoSmallSvg);

const SHEET_GAP_PX = 24;

type Props = {
  users: User[];
};

const ProfilePickerContent: FunctionComponent<Props> = ({ users }) => {
  const { greeting, greetingIndex } = useBaoGreeting();
  const { selectProfile, addProfile } = useFamilyProfileSwitch(users);
  const [panelHeight, setPanelHeight] = useState(0);
  const isLaunchChoice = useIsProfileChoiceNeeded();

  useEffect(() => {
    if (users.length) return;
    reportError('Profile picker without any named family member');
    markProfileChosen();
  }, [users.length]);

  return (
    <div
      className="flex flex-1 flex-col items-center justify-center gap-3 px-4 pt-4 motion-safe:animate-profile-picker-fade-in"
      style={{ paddingBottom: panelHeight + SHEET_GAP_PX }}
    >
      <div className="mb-2.5 origin-bottom motion-safe:animate-profile-bubble-in">
        <TourTooltip variant="speech">{greeting}</TourTooltip>
      </div>
      <div className="motion-safe:animate-profile-bao-in">
        <div className="motion-safe:animate-profile-bao-idle">
          <BaoSmall className="w-[104px] h-[104px]" />
        </div>
      </div>
      <UserSelect
        isOpen
        isClosable={false}
        isEntranceAnimated
        users={users}
        onSelectUser={selectProfile}
        onAddNewUser={addProfile}
        onPanelHeightChange={setPanelHeight}
        analyticsSource={
          isLaunchChoice ? 'app_launch' : 'profile_unknown_on_device'
        }
        greetingIndex={greetingIndex}
      />
    </div>
  );
};

const ProfilePicker = () => {
  const usersQuery = useGetUsers();

  return (
    <PageLayout variant="white" fitContent>
      <QueryGate
        queries={[usersQuery]}
        errorFallback={retry => (
          <QueryErrorState onRetry={retry} screen="profile_picker" />
        )}
      >
        {() => (
          <ProfilePickerContent
            users={(usersQuery.data ?? []).filter(user => user.firstname?.length)}
          />
        )}
      </QueryGate>
    </PageLayout>
  );
};

export default ProfilePicker;
